import { useCallback } from 'react';
import { AngledHighwayElement, LinePathElement } from '@thive/canvas';
import { Areas, HighwayArea, Obstacle, Segment } from '@thive/fddm-types';
import { Vector3 } from 'three';

import { IdUtils } from '@/modules/common/utils';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { VectorUtils } from '@/modules/common/utils/VectorUtils';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { isHighwayArea, pushAreaToItsCollection } from '@/modules/floorplanDesign/helpers';
import { AtLeast1, isAtLeast1 } from '@/modules/floorplanDesign/helpers/minItems';
import { FDDM_TAG } from '@/modules/floorplanDesign/constants';
import { createEmptyAreas } from '@/modules/floorplanDesign/helpers/helpers';

import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { useCanvasStore } from '../store';
import { logger } from '@common/logging/logger';

export const useEditEnd = () => {
  const { getAreaById, getObstacleById } = useFloorplanDesignStore();
  const selectedIds = useAppStore((state) => state.selectedIds);
  const fddmSvc = FddmServiceFactory.getInstance().createFddmService();

  const updateSegmentsPoints = (
    segments: AtLeast1<Segment>,
    points: Vector3[],
  ): AtLeast1<Segment> => {
    const { height } = segments[0];
    const width = segments[0].leftSideWidth + segments[0].rightSideWidth;
    const id = IdUtils.destructureIdWithSegmentIndex(segments[0].id).areaId;

    const newSegments: Segment[] = [];
    for (let i: number = 0; i < points.length - 1; i++) {
      const start = VectorUtils.mmToMeters(points[i]);
      const end = VectorUtils.mmToMeters(points[i + 1]);
      newSegments.push({
        id: IdUtils.buildIdWithSegmentIndex(id, i),
        leftSideWidth: width / 2,
        rightSideWidth: width / 2,
        start: { x: start.x, y: start.y },
        end: { x: end.x, y: end.y },
        height,
      });
    }

    if (!isAtLeast1(newSegments)) {
      return segments;
    }

    return newSegments;
  };

  const updateArea = useCallback(
    (areaElement: AngledHighwayElement, correspondingArea: HighwayArea, areaUpdates: Areas) => {
      if (!areaElement.position) {
        // error for dev purpose. Remove later. Canvas should guarantee that position property exists
        logger.error('Canvas element has undefined position');
        return;
      }
      if (!areaElement.rotation) {
        // error for dev purpose. Remove later. Canvas should guarantee that rotation property exists
        logger.error('Canvas element has undefined rotation');
        return;
      }
      if (!areaElement.points || areaElement.points.length < 2) {
        // error for dev purpose. Remove later. Canvas should guarantee that points property exists
        logger.error('Canvas element has undefined points');
        return;
      }

      const segments = updateSegmentsPoints(correspondingArea.segments, areaElement.points);
      if (!segments) return;

      const updatedArea = {
        ...correspondingArea,
        segments,
      };

      pushAreaToItsCollection(areaUpdates, updatedArea);
    },
    [],
  );

  const updateObstacle = useCallback(
    (obstacleElement: LinePathElement, correspondingObstacle: Obstacle) => {
      if (!obstacleElement.position) {
        // error for dev purpose. Remove later. Canvas should guarantee that position property exists
        logger.error('Canvas element has undefined position');
        return;
      }
      if (!obstacleElement.rotation) {
        // error for dev purpose. Remove later. Canvas should guarantee that rotation property exists
        logger.error('Canvas element has undefined rotation');
        return;
      }
      if (!obstacleElement.points || obstacleElement.points.length < 2) {
        // error for dev purpose. Remove later. Canvas should guarantee that points property exists
        logger.error('Canvas element has undefined points');
        return;
      }

      const segments = updateSegmentsPoints(correspondingObstacle.segments, [
        ...obstacleElement.points,
      ]);
      if (!segments) return;

      return {
        ...correspondingObstacle,
        segments,
      };
    },
    [],
  );

  const editEnded = useCallback(() => {
    if (selectedIds.length !== 1) return;

    const canvas = useCanvasStore.getState().instance;
    if (!canvas) return;

    const id = selectedIds[0];
    const element = canvas.getElement(id);
    if (!element) return;

    if (element.type === 'AngledHighway' && element.tags?.includes(FDDM_TAG.HIGHWAY_AREA)) {
      const area = getAreaById(id);
      if (area === undefined || !isHighwayArea(area)) {
        return;
      }

      const areaUpdates = createEmptyAreas();
      updateArea(element, area, areaUpdates);
      fddmSvc.updateAreas(areaUpdates);
    } else if (element.type === 'LinePath' && element.tags?.includes(FDDM_TAG.WALL)) {
      const obstacle = getObstacleById(id);
      if (obstacle === undefined) return;

      const updatedObstacle = updateObstacle(element, obstacle);
      if (updatedObstacle) {
        fddmSvc.updateObstacle(updatedObstacle);
      }
    }
  }, [selectedIds, getAreaById, updateArea, fddmSvc, getObstacleById, updateObstacle]);

  return { editEnded };
};
